import { Component } from '@nicolas-riciotti/light2';

// Bundled charts: slug -> dynamic import of a module in src/scripts/charts.
// Left empty on purpose — every chart on this site is a drop-in served from the
// theme's /charts folder (see includes/charts.php), which needs no rebuild. Add
// an entry here only if a chart has to ship inside the Vite bundle.
const CHARTS = {};

export default class BlockChart extends Component {
  attached() {
    // A mobile image replaces the interactive chart below 600px (render.php
    // swaps visibility with m:!hidden / m:!block), so skip the D3 work
    // entirely on phones. If the viewport later grows past the breakpoint,
    // arm the chart then — the container was never measured while hidden.
    const mobileSwap = this.hasAttribute('data-chart-mobile-image')
      ? window.matchMedia('(max-width: 600px)')
      : null;
    if (mobileSwap && mobileSwap.matches) {
      mobileSwap.addEventListener('change', (e) => {
        if (!e.matches) this.attached();
      }, { once: true });
      return;
    }

    const container = this.querySelector('[data-chart-container]');
    if (!container) return;

    const CHART_KEY = this.getAttribute('data-chart-name');
    // Drop-in charts: a raw ES module URL served from the theme /charts
    // folder. Takes precedence over the bundled CHARTS map so new charts
    // (or hotfixes of bundled ones) work without a rebuild.
    const CHART_URL = this.getAttribute('data-chart-url');
    const title = this.getAttribute('data-chart-title');
    const subtitle = this.getAttribute('data-chart-subtitle');

    let json;
    if (this.hasAttribute('data-chart-json')) {
      try {
        json = JSON.parse( this.getAttribute('data-chart-json') );
      } catch (e) {
        // A malformed JSON field must not take the chart down — fall back to
        // whatever defaults the chart module ships with.
        console.warn('BLOCK_CHART_BAD_JSON', CHART_KEY, e);
      }
    }

    const loadChart = CHART_URL
      ? () => import(/* @vite-ignore */ CHART_URL)
      : CHARTS[CHART_KEY];
    if (!loadChart) return;

    let d3;
    let utils;
    let render;

    let scrollTimer;
    let preBuilt = false;
    let rendered = false;
    let resizeObserver;
    let lastWidth;
    let renderTimer = null;
    let chartPlay = null;
    let loadingPromise = null;

    let stopped = localStorage['accessibility'] === 'true';

    async function loadDeps() {
      if (loadingPromise) return loadingPromise;

      loadingPromise = Promise.all([
        import('d3'),
        import('../../charts/utils.js'),
        loadChart(),
      ]).then(([d3Module, utilsModule, chartModule]) => {
        d3 = d3Module;
        utils = utilsModule;
        render = chartModule.render;
      }).catch((e) => {
        console.warn('BLOCK_CHART_LOAD_FAILED', CHART_KEY, CHART_URL, e);
      });

      return loadingPromise;
    }

    async function _doRender(playIntro) {
      await loadDeps();

      if (!render) return;

      container.innerHTML = '';

      const width = container.clientWidth;
      container.style.overflow = 'visible';

      chartPlay = render({
        title,
        subtitle,
        json,
        container,
        width,
        d3,
        playIntro,
        animate: !stopped,
        utils,
      });

      container.querySelectorAll('svg').forEach((svg) => {
        svg.style.overflow = 'visible';
      });

      container.querySelectorAll('svg text').forEach((el) => {
        // Charts can opt subtrees out of the mono override by adding a
        // .keep-font class to an ancestor <g> (used when labels should
        // render in the site's brand serif instead).
        if (el.closest('.keep-font')) return;
        el.setAttribute('font-family', "'Red Hat Mono', monospace");
      });
    }

    function _render(playIntro) {
      clearTimeout(renderTimer);

      renderTimer = setTimeout(async () => {
        await _doRender(playIntro);
        if (chartPlay) chartPlay();
      }, 100);
    }

    function siteSettingsMotionChange(event) {
      stopped = event.detail === 'off';
      _render(!stopped);
    }

    document.addEventListener('site_settings_motion', siteSettingsMotionChange, false);

    const preObserver = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !preBuilt) {
        preBuilt = true;
        preObserver.disconnect();
        _doRender(true);
      }
    }, {
      threshold: 0.01,
      rootMargin: '0px 0px 300px 0px',
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting && !rendered) {
          rendered = true;
          observer.disconnect();

          if (!chartPlay) await _doRender(true);

          container.style.visibility = '';

          if (chartPlay) chartPlay();

          lastWidth = container.clientWidth;

          resizeObserver = new ResizeObserver(() => {
            const newWidth = container.clientWidth;

            if (newWidth !== lastWidth) {
              lastWidth = newWidth;
              _render(false);
            }
          });

          resizeObserver.observe(container);

          window.addEventListener('scroll', () => {
            container.style.pointerEvents = 'none';

            clearTimeout(scrollTimer);

            scrollTimer = setTimeout(() => {
              container.style.pointerEvents = '';
            }, 500);
          }, { passive: true });
        }
      });
    }, {
      threshold: 0.15,
    });

    preObserver.observe(container);
    observer.observe(container);
  }
}

customElements.define('block-chart', BlockChart);
